import React from 'react'

import { cn } from 'lib/utils'

/**
 * Ask MDT's answers, formatted (MYD-621).
 *
 * The model writes a little markdown: paragraphs, short lists, bold, inline
 * code, the odd heading, and tables when comparing sites. This renders exactly
 * that subset as React elements. Never as HTML: an answer quotes record data
 * people typed, so nothing in it may become markup. Anything outside the
 * subset shows as the text it is.
 *
 * Renders while streaming, so a half-arrived table or `**` is drawn as plain
 * text until the rest arrives, never as broken markup.
 */

type Block =
	| { kind: 'heading'; level: number; text: string }
	| { kind: 'list'; ordered: boolean; items: string[] }
	| { kind: 'table'; header: string[]; rows: string[][] }
	| { kind: 'paragraph'; text: string }

const BULLET = /^\s*[-*•]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/
const HEADING = /^(#{1,4})\s+(.*)$/
const TABLE_ROW = /^\s*\|.*\|\s*$/
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/

const cells = (row: string) =>
	row
		.trim()
		.replace(/^\|/, '')
		.replace(/\|$/, '')
		.split('|')
		.map(cell => cell.trim())

/** The answer as blocks. Exported for tests. */
export const parseBlocks = (source: string): Block[] => {
	const lines = source.replace(/\r\n?/g, '\n').split('\n')
	const blocks: Block[] = []
	let index = 0

	while (index < lines.length) {
		const line = lines[index]
		if (!line.trim()) {
			index += 1
			continue
		}

		const heading = line.match(HEADING)
		if (heading) {
			blocks.push({ kind: 'heading', level: heading[1].length, text: heading[2] })
			index += 1
			continue
		}

		// A table needs its rule line; until it arrives the rows are a paragraph.
		if (TABLE_ROW.test(line) && TABLE_RULE.test(lines[index + 1] ?? '')) {
			const header = cells(line)
			const rows: string[][] = []
			index += 2
			while (index < lines.length && TABLE_ROW.test(lines[index])) rows.push(cells(lines[index++]))
			blocks.push({ kind: 'table', header, rows })
			continue
		}

		const ordered = NUMBERED.test(line)
		if (ordered || BULLET.test(line)) {
			const pattern = ordered ? NUMBERED : BULLET
			const items: string[] = []
			while (index < lines.length && pattern.test(lines[index])) items.push(lines[index++].match(pattern)![1])
			blocks.push({ kind: 'list', ordered, items })
			continue
		}

		const paragraph: string[] = []
		while (
			index < lines.length &&
			lines[index].trim() &&
			!HEADING.test(lines[index]) &&
			!BULLET.test(lines[index]) &&
			!NUMBERED.test(lines[index]) &&
			!(TABLE_ROW.test(lines[index]) && TABLE_RULE.test(lines[index + 1] ?? ''))
		) {
			paragraph.push(lines[index++])
		}
		blocks.push({ kind: 'paragraph', text: paragraph.join('\n') })
	}
	return blocks
}

/** **bold** and `code`, as elements. Anything else is text. Exported for tests. */
export const renderInline = (text: string): React.ReactNode[] =>
	text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, index) => {
		if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={index}>{part.slice(2, -2)}</strong>
		if (/^`[^`]+`$/.test(part)) return (
			<code key={index} className="rounded bg-muted px-1 py-0.5 text-[0.85em]">
				{part.slice(1, -1)}
			</code>
		)
		return <React.Fragment key={index}>{part}</React.Fragment>
	})

export function AskMdtMarkdown({ text, className }: { text: string; className?: string }) {
	return (
		<div className={cn('space-y-2 text-sm leading-relaxed text-gray-800', className)}>
			{parseBlocks(text).map((block, index) => {
				switch (block.kind) {
					case 'heading':
						return (
							<p key={index} className="font-semibold text-gray-900">
								{renderInline(block.text)}
							</p>
						)
					case 'list': {
						const List = block.ordered ? 'ol' : 'ul'
						return (
							<List key={index} className={cn('space-y-1 pl-5', block.ordered ? 'list-decimal' : 'list-disc')}>
								{block.items.map((item, itemIndex) => (
									<li key={itemIndex}>{renderInline(item)}</li>
								))}
							</List>
						)
					}
					case 'table':
						return (
							<div key={index} className="overflow-x-auto rounded-md border border-gray-200">
								<table className="w-full text-left text-xs">
									<thead className="bg-gray-50">
										<tr>
											{block.header.map((cell, cellIndex) => (
												<th key={cellIndex} className="px-2 py-1.5 font-semibold text-gray-700">
													{renderInline(cell)}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{block.rows.map((row, rowIndex) => (
											<tr key={rowIndex} className="border-t border-gray-100">
												{row.map((cell, cellIndex) => (
													<td key={cellIndex} className="px-2 py-1.5 align-top">
														{renderInline(cell)}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)
					default:
						return (
							<p key={index} className="whitespace-pre-wrap">
								{renderInline(block.text)}
							</p>
						)
				}
			})}
		</div>
	)
}

export default AskMdtMarkdown
